<template>
    <DModal
        :title="modalHeader"
        is-big
        :show="showModal"
        @close="closeModal"
        @dismis="closeModal"
    >
        <div class="table-padding">
            <DTable
                :fields="fields"
                :items="tableItems"
            >
                <template #price="{item}">
                    <td :class="{'text-warning' : item.priceUpdated}">
                        {{ priceFormatForDisplay(item.price, 3, customer.market_region_id) }}
                    </td>
                </template>
                <template #discount="{item}">
                    <td>
                        <span :class="{'text-warning' : item.discountPercentageUpdated}">
                            {{ percentageFormatForDisplay(item.discountPercentage) }}
                        </span>
                        /
                        <span :class="{'text-warning' : item.discountFixedUpdated}">
                            {{ priceFormatForDisplay(item.discountFixed, 3, customer.market_region_id) }}
                        </span>
                    </td>
                </template>
                <template #productName="{item}">
                    <td :class="{'text-warning' : item.productNameUpdated}">
                        {{ item.productName }}
                    </td>
                </template>
                <template #setupFee="{item}">
                    <td :class="{'text-warning' : item.setupFeeUpdated}">
                        {{ priceFormatForDisplay(item.setupFee, 3, customer.market_region_id) }}
                    </td>
                </template>
                <template #billingCycle="{item}">
                    <td :class="{'text-warning' : item.billingCycleUpdated}">
                        {{ item.billingCycle }}
                    </td>
                </template>
                <template #thirdPartyCost="{item}">
                    <td :class="{'text-warning' : item.thirdPartyCostUpdated}">
                        {{ priceFormatForDisplay(item.thirdPartyCost, 3, customer.market_region_id) }}
                    </td>
                </template>
                <template #nettPrice="{item}">
                    <td>
                        {{ priceFormatForDisplay(item.nettPrice, 2, customer.market_region_id) }}
                    </td>
                </template>
                <template #grossPrice="{item}">
                    <td>
                        {{ priceFormatForDisplay(item.grossPrice, 2, customer.market_region_id) }}
                    </td>
                </template>
                <template #phase_text="{item}">
                    <td>
                        {{ item.phase_text ?? "" }}
                    </td>
                </template>
                <template #action="{item}">
                    <td>
                        <DButton
                            v-if="canBeDeleted(item) === true && previewMode === false"
                            :id="`btn-delete-${item.id}`"
                            icon="trash-can-outline"
                            color="danger"
                            mini
                            @click="deletePhase(item)"
                        />
                    </td>
                </template>
            </DTable>
        </div>

        <div class="table-padding price-form">
            <DFormRow
                :label="__('customers.contracts.committed_to')"
                class="mb-0"
            >
                <span v-if="contract.billed_until_date">{{ setDateFormatEuropeBerlin(contract.billed_until_date) }}</span>
                <DTag
                    v-else
                    :label="__('customers.contracts.no_billing')"
                />
                <span
                    v-if="contract.next_invoice_date !== null && !isExcludedFromInvoicing()"
                    class="font-weight-normal"
                >
                    ({{ contract.has_invoices ? __('customers.contracts.next_invoice') : __('customers.contracts.first_invoice') }} {{ setDateFormatEuropeBerlin(contract.next_invoice_date) }})
                </span>
                <span
                    v-else-if="isExcludedFromInvoicing()"
                >
                    ({{ __('customers.contracts.invoicing_disabled') }})
                </span>
            </DFormRow>

            <div
                v-if="previewMode === false"
                class="p-0"
            >
                <h4 class="pb-2">
                    {{ __('customers.contracts.price_overview_form.header') }}
                </h4>

                <DForm @submit.prevent="">
                    <DRow>
                        <DCol col="6">
                            <!-- Billing Cycle -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.billing_cycle')"
                                :is-required="true"
                            >
                                <DSelect
                                    id="billing_cycle"
                                    v-model="form.billing_cycle"
                                    :options="billingCycles"
                                    :disabled="previewMode"
                                    @update:value="updateFormValuesAfterCycleChange()"
                                />
                                <p
                                    v-if="errors.billing_cycle"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.billing_cycle[0] }}
                                </p>
                            </DFormRow>

                            <!-- Price -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.price')"
                                :is-required="true"
                            >
                                <DInput
                                    id="price"
                                    v-model="form.price"
                                    :disabled="previewMode || !userPermissions.includes('price.edit')"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.price"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.price[0] }}
                                </p>
                            </DFormRow>
                            <!-- Setup fee -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.setup_fee')"
                                :is-required="false"
                            >
                                <DInput
                                    id="setup_fee"
                                    v-model="form.setup_fee"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.setup_fee"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.setup_fee[0] }}
                                </p>
                            </DFormRow>

                            <!-- Start date -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.start_date')"
                                :is-required="true"
                            >
                                <DDateFormatInput
                                    id="start_date"
                                    v-model="form.start_date"
                                    format="YYYY-MM-DD"
                                    :disabled="previewMode"
                                    type="date"
                                    @valueChanged="updateFormValues"
                                />
                                <p
                                    v-if="errors.start_date"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.start_date[0] }}
                                </p>
                            </DFormRow>

                            <!-- Overwrite planed price settings -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.overwrite_planned_price')"
                                :is-required="false"
                            >
                                <DFormCheck
                                    id="preview_mode"
                                    v-model="form.overwrite_planned_price"
                                    :disabled="previewMode"
                                    @update:modelValue="updateFormValues"
                                />
                                <p
                                    v-if="errors.overwrite_planned_price"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.overwrite_planned_price[0] }}
                                </p>
                            </DFormRow>
                            <!-- Nett price -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.nett_price')"
                            >
                                <DInput
                                    id="nett_price"
                                    v-model="form.nett_price"
                                    disabled
                                />
                            </DFormRow>
                        </DCol>
                        <DCol col="6">
                            <!-- Third party cost -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.third_party_cost')"
                                :is-required="false"
                            >
                                <DInput
                                    id="third_party_cost"
                                    v-model="form.third_party_cost"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.third_party_cost"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.third_party_cost[0] }}
                                </p>
                            </DFormRow>

                            <!-- Discount name -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.discount_name')"
                                :is-required="false"
                            >
                                <DInput
                                    id="discount_name"
                                    v-model="form.discount_name"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.discount_name"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.discount_name[0] }}
                                </p>
                            </DFormRow>

                            <!-- Discount type -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.discount_type')"
                                :is-required="false"
                            >
                                <DSelect
                                    id="discount_type"
                                    v-model="form.discount_type"
                                    :options="discountTypes"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.discount_type"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.discount_type[0] }}
                                </p>
                            </DFormRow>

                            <!-- Discount value -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.discount_value')"
                                :is-required="false"
                            >
                                <DInput
                                    id="discount_value"
                                    v-model="form.discount_value"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.discount_value"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.discount_value[0] }}
                                </p>
                            </DFormRow>

                            <DFormRow
                                :label="__('customers.contracts.phase_text')"
                            >
                                <DInput
                                    id="phase_text"
                                    v-model="form.phase_text"
                                    :disabled="previewMode"
                                    @update:value="updateFormValues"
                                />
                                <p
                                    v-if="errors.phase_text"
                                    class="m-0 pt-1 text-danger"
                                >
                                    {{ errors.third_party_cost[0] }}
                                </p>
                            </DFormRow>
                            <!-- Gross price -->
                            <DFormRow
                                :label="__('customers.contracts.price_overview_form.gross_price')"
                                class="mt-4"
                            >
                                <DInput
                                    id="gross_price"
                                    v-model="form.gross_price"
                                    disabled
                                />
                            </DFormRow>
                        </DCol>
                    </DRow>
                </DForm>
            </div>
        </div>
        <div
            v-if="showWarningMessage"
            class="vertical-padding table-padding w-100"
        >
            <p class="w-100 py-4 px-2 warning-block rounded mx-auto text-center">
                {{ __('customers.contracts.inclusive_domains.warning', {usedInclusiveDomains: usedInclusiveDomains}) }}
            </p>
        </div>
        <div class="table-padding pb-3 d-flex gap-2 text-right">
            <div
                v-if="(formDataUpdated || deletionDataUpdated) && previewMode === true"
                class="width-100"
            >
                <DFormTextarea
                    id="change_reason"
                    v-model="form.activityLogComment"
                    class="d-inline-block"
                    :placeholder="__('messages.change_reason')"
                    add-input-classes="bg-light"
                    :is-valid="isFormFieldValid('activity_log_comment')"
                    :invalid-feedback="errors.activity_log_comment ? errors.activity_log_comment[0] : null"
                    maxlength="1000"
                />
                <div
                    v-if="incidents.length > 1 && userPermissions.includes('customer.incidents_view') && userPermissions.includes('customer.incidents_manage')"
                    class="incident-class"
                >
                    <span>{{ __('customers.business_incidents.add_to_incident') }}</span>
                    <DSelect
                        id="add_incident"
                        v-model="form.incident_id"
                        :options="incidents"
                        :is-valid="isFormFieldValid('incident_id')"
                    />
                </div>
                <p
                    v-if="errors.incident_id"
                    class="error-msg"
                >
                    {{ errors.incident_id }}
                </p>
            </div>
            <div>
                <DButton
                    v-if="formDataUpdated"
                    :label="showPreviewLabel"
                    :color="'primary'"
                    :loading="isLoading"
                    :disabled="isLoading"
                    @click="getPriceOverviewData"
                />
                <DButton
                    v-if="deletionDataUpdated"
                    :label="showPreviewLabel"
                    :color="'primary'"
                    :loading="isLoading"
                    :disabled="isLoading"
                    @click="getPriceOverviewDataBeforeDeletion"
                />
                <DButton
                    v-if="previewMode"
                    :label="__('customers.contracts.decline_changes')"
                    :color="'danger'"
                    :loading="isLoading"
                    :disabled="isLoading"
                    class="mt-2"
                    @click="resetPriceOverview"
                />
            </div>
        </div>
    </DModal>
</template>

<script>

import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'PriceOverviewModal',
    mixins: [
        priceFormat
    ],
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        groupedContractPhases: {
            type: Array,
            default: () => { return [] }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        billingCycles: {
            type: Array,
            default: () => { return [] }
        },
        taxRate: {
            type: Number,
            default: () => { return 0 }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['close-modal'],
    data () {
        return {
            fields: [
                { key: 'startDate', label: this.__('customers.contracts.price_overview_table.from') },
                { key: 'endDate', label: this.__('customers.contracts.price_overview_table.to') },
                { key: 'price', label: this.__('customers.contracts.price_overview_table.price') },
                { key: 'discount', label: this.__('customers.contracts.price_overview_table.discount') },
                { key: 'discountName', label: this.__('customers.contracts.price_overview_table.discount_name') },
                { key: 'productName', label: this.__('customers.contracts.price_overview_table.product_name') },
                { key: 'setupFee', label: this.__('customers.contracts.price_overview_table.setup_fee') },
                { key: 'billingCycle', label: this.__('customers.contracts.price_overview_table.billing_cycle') },
                { key: 'thirdPartyCost', label: this.__('customers.contracts.price_overview_table.third_party_cost') },
                { key: 'nettPrice', label: this.__('customers.contracts.price_overview_table.nett') },
                { key: 'grossPrice', label: this.__('customers.contracts.price_overview_table.gross') },
                { key: 'phase_text', label: this.__('customers.contracts.phase_text') },
                { key: 'action', label: '' }
            ],
            form: {
                id: null,
                contract_id: null,
                start_date: null,
                price: null,
                billing_cycle: null,
                discount_name: null,
                discount_type: null,
                discount_value: null,
                third_party_cost: null,
                setup_fee: null,
                nett_price: null,
                gross_price: null,
                overwrite_planned_price: null,
                activityLogComment: '',
                incident_id: null
            },
            tableItems: [],
            oldBillingCycle: 1,
            errors: {},
            previewMode: false,
            formDataUpdated: false,
            oldTableItems: [],
            rawTableItems: [],
            isLoading: false,
            showWarningMessage: false,
            incidents: [],
            deletionDataUpdated: false,
            phaseForDeletion: null
        }
    },
    computed: {
        modalHeader () {
            return this.__('customers.contracts.price_overview_header') + this.contract.id
        },
        activeContractPhase () {
            return this.contract.active_contract_phase
        },
        showPreviewLabel () {
            if (!this.previewMode) return this.__('customers.contracts.price_preview_mode')
            return this.__('customers.contracts.accept_changes')
        },
        usedInclusiveDomains () {
            let string = ''
            for (const [usedDomains, usedValue] of Object.entries(this.contract.parent_inclusive_domains.used_inclusive_domains)) {
                for (const [possibleDomains, possibleValue] of Object.entries(this.contract.parent_inclusive_domains.possible_inclusive_domains)) {
                    if (possibleDomains === usedDomains) {
                        string += usedValue + ' ' +
                            this.__('customers.contracts.inclusive_domains.out_of') + ' ' +
                            possibleValue +
                            ' (' + usedDomains + ') ' +
                            this.__('customers.contracts.inclusive_domains.and') + ' '
                    }
                }
            }

            // Remove and or und from and of string
            return string.substring(0, string.length - 4)
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    created () {
        this.rawTableItems = this.groupedContractPhases
        this.updateTableItems()
    },
    mounted () {
        this.fillDefaultFormData()
        this.oldBillingCycle = this.activeContractPhase.billing_cycle
        if (this.contract.customer_id && this.userPermissions.includes('customer.incidents_view') && this.userPermissions.includes('customer.incidents_manage')) {
            window.axios.get(this.$route('customers.customers.get-customer-open-business-incidents-array', this.contract.customer_id))
                .then(response => {
                    this.incidents = response.data.incidents
                })
        }
    },
    methods: {
        fillDefaultFormData () {
            this.form.id = this.activeContractPhase.id
            this.form.start_date = this.initialStartDate()
            this.form.price = this.numberFormatForInput(this.activeContractPhase.price)
            this.form.billing_cycle = this.activeContractPhase.billing_cycle
            this.form.discount_name = this.activeContractPhase.discount_name
            this.form.discount_type = this.activeContractPhase.discount_type
            this.form.discount_value = this.activeContractPhase.discount_value ? this.numberFormatForInput(this.activeContractPhase.discount_value) : null
            this.form.overwrite_planned_price = false
            this.form.third_party_cost = this.numberFormatForInput(this.activeContractPhase.third_party_cost)
            this.form.setup_fee = this.numberFormatForInput(this.activeContractPhase.setup_fee)
            this.form.nett_price = this.priceFormatForDisplay(this.updatePrice(), 2, this.customer.market_region_id)
            this.form.gross_price = this.priceFormatForDisplay(this.updatePrice(this.taxRate), 2, this.customer.market_region_id)
            this.form.phase_text = this.activeContractPhase.phase_text
        },
        initialStartDate () {
            let startDate
            if (this.contract.billed_until_date) {
                startDate = new Date(this.contract.billed_until_date)
                startDate.setDate(startDate.getDate() + 1)
            } else {
                startDate = new Date()
            }

            return startDate.toISOString().split('T')[0]
        },
        updateTableItems () {
            const items = []
            for (const groupLine of this.rawTableItems) {
                if (groupLine.contractPhaseIds.includes(this.activeContractPhase.id)) {
                    Object.assign(groupLine, { _classes: 'table-success' })
                }
                items.push({ ...groupLine, price: this.numberFormatForInput(groupLine.price) })
            }

            this.tableItems = items
        },
        closeModal () {
            this.fillDefaultFormData()
            this.previewMode = false
            this.errors = {}
            this.form.activityLogComment = ''
            this.form.incident_id = null
            this.$emit('close-modal')
        },
        updatePrice (taxRate = 0) {
            return (this.internationalToNormalFormat(this.form.price) - this.calculateDiscount()) * (1 + taxRate / 100)
        },
        calculateDiscount () {
            return this.form.discount_type === 'fixed'
                ? this.internationalToNormalFormat(this.form.discount_value)
                : (this.internationalToNormalFormat(this.form.discount_value) / 100) * this.internationalToNormalFormat(this.form.price)
        },
        updateFormValuesAfterCycleChange () {
            const billingCycleRatio = this.form.billing_cycle / this.oldBillingCycle

            if (this.form.discount_type === 'fixed') {
                this.form.discount_value = this.internationalToNormalFormat(this.form.discount_value) * billingCycleRatio
            }

            this.form.price = this.internationalToNormalFormat(this.form.price) * billingCycleRatio
            this.form.start_date = this.initialStartDate()

            this.updateFormValues()
            this.oldBillingCycle = this.form.billing_cycle
        },
        updateFormValues () {
            this.form.nett_price = this.priceFormatForDisplay(this.updatePrice(), 2, this.customer.market_region_id)
            this.form.gross_price = this.priceFormatForDisplay(this.updatePrice(this.taxRate), 2, this.customer.market_region_id)
            this.formDataUpdated = this.checkIfFormIsUpdated()
        },
        resetPriceOverview () {
            this.previewMode = false
            this.rawTableItems = this.oldTableItems
            this.formDataUpdated = false
            this.deletionDataUpdated = false
            this.updateTableItems()
        },
        checkIfFormIsUpdated () {
            return this.form.start_date !== this.activeContractPhase.start_date ||
            this.internationalToNormalFormat(this.form.price) !== this.activeContractPhase.price ||
            this.form.billing_cycle !== this.activeContractPhase.billing_cycle ||
            this.internationalToNormalFormat(this.form.third_party_cost) !== this.activeContractPhase.third_party_cost ||
            this.form.discount_name !== this.activeContractPhase.discount_name ||
            this.form.discount_type !== this.activeContractPhase.discount_type ||
            this.internationalToNormalFormat(this.form.discount_value) !== this.activeContractPhase.discount_value ||
            this.internationalToNormalFormat(this.form.setup_fee) !== this.activeContractPhase.setup_fee ||
            this.form.overwrite_planned_price !== false
        },
        getPriceOverviewData () {
            this.showWarningMessage = false
            this.isLoading = true
            this.errors = {}
            window.axios.post(this.$route('customers.customers.contracts.price_overview', {
                customer: this.contract.customer_id,
                contract: this.contract
            }), {
                recalculate: 1,
                start_date: this.form.start_date,
                price: this.form.price,
                billing_cycle: this.form.billing_cycle,
                third_party_cost: this.form.third_party_cost,
                setup_fee: this.form.setup_fee,
                discount_name: this.form.discount_name,
                discount_type: this.form.discount_type,
                discount_value: this.form.discount_value,
                overwrite_planned_price: this.form.overwrite_planned_price,
                save_phase: this.previewMode,
                activity_log_comment: this.form.activityLogComment,
                incident_id: this.form.incident_id,
                phase_text: this.form.phase_text
            })
                .then((response) => {
                    if (!this.previewMode) {
                        this.oldTableItems = this.rawTableItems
                    }
                    if (this.previewMode) {
                        this.formDataUpdated = false
                    }
                    this.previewMode = !this.previewMode
                    this.rawTableItems = response.data.groupedContractPhases

                    if (this.previewMode) {
                        this.showWarningMessage = response.data.inclusiveWarning
                    }

                    this.updateTableItems()
                })
                .catch((error) => {
                    if (error && error.response && error.response.data) {
                        this.errors = error.response.data.errors
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        // Same single source of truth as the contract page — see Contracts/Show.vue.
        // `contract` is the very object that page holds, so the field is always present.
        isExcludedFromInvoicing () {
            return Boolean(this.contract.invoicing_exclusion)
        },
        canBeDeleted (item) {
            if (this.activeContractPhase.id === item.id) {
                return false
            }

            if (this.contract.billed_until_date !== null &&
                new Date(this.contract.billed_until_date) > new Date(item.startDate)
            ) {
                return false
            }

            if (new Date(this.activeContractPhase.start_date) > new Date(item.startDate)) {
                return false
            }

            return true
        },
        deletePhase (item) {
            if (item.id !== null) {
                this.deletionDataUpdated = true
                this.phaseForDeletion = item.id
            }

            this.getPriceOverviewDataBeforeDeletion()
        },
        getPriceOverviewDataBeforeDeletion () {
            this.showWarningMessage = false
            this.isLoading = true
            this.errors = {}
            window.axios.post(this.$route('customers.customers.contracts.price_deletion_overview', {
                customer: this.contract.customer_id,
                contract: this.contract
            }), {
                delete: this.previewMode,
                activity_log_comment: this.form.activityLogComment,
                incident_id: this.form.incident_id,
                phase_for_deletion: this.phaseForDeletion
            })
                .then((response) => {
                    if (!this.previewMode) {
                        this.oldTableItems = this.rawTableItems
                    }
                    if (this.previewMode) {
                        this.phaseForDeletion = null
                        this.deletionDataUpdated = false
                    }
                    this.previewMode = !this.previewMode
                    this.rawTableItems = response.data.groupedContractPhases

                    this.updateTableItems()
                })
                .catch((error) => {
                    if (error && error.response && error.response.data) {
                        this.errors = error.response.data.errors
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    }
}
</script>
<style lang="scss">
.table-padding {
    padding-left: 20px;
    padding-right: 20px;
}
.vertical-padding {
    padding-top: 40px;
    padding-bottom: 20px;
}
.bottom-padding {
    padding-bottom: 20px;
    padding-left: 20px;
    padding-right: 20px;
}

.warning-block {
    background-color: #f77b83;
}
.width-100 {
    width: 100%;
}
.price-form div.d-form-row {
    margin-bottom: 15px !important;
}
</style>
